/** @jsx jsx */
import type { SerializedStyles } from '@emotion/react';
import { jsx } from '@emotion/react';

import { useCallback, useEffect } from 'react';

import type { ApolloError } from 'apollo-client';

import type { CourseItem, Product } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachChat } from 'bundles/ai-coach-platform';
import {
  addResponseMessage,
  dropMessages,
  enableMsgLoader,
  sendMessage as sendMessagePlatform,
  setChatSessionId,
} from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import type { CoachModeType } from 'bundles/ai-coach-platform/types';
import EmptyState from 'bundles/ai-course-coach/components/HomeView/EmptyState';
import { getErrorMessageText } from 'bundles/ai-course-coach/components/constants';
import { getCoachPendoTrigger } from 'bundles/ai-course-coach/utils/pendo';
import { isCoachAudioEnabled, isIntentionRoutingEnabled } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import { captureMessageInSentry } from 'bundles/ai-course-coach/utils/sentryUtils';
import type { HomeView as HomeViewType } from 'bundles/ai-course-coach/utils/types';
import { useCourseToolBarPlugins } from 'bundles/course-v2/components/navigation/CourseToolBar';

export type Props = {
  itemId?: string;
  courseId?: string;
  mode: CoachModeType;
  homeView?: HomeViewType;
  onClose?: () => void;
  cssOverrides?: SerializedStyles;
  disableSpeechToText?: boolean;
  speechToTextDisabledTooltip?: string;
};

function handleError(apolloError: ApolloError) {
  enableMsgLoader(false);
  addResponseMessage({ text: getErrorMessageText() });
  if (apolloError.graphQLErrors && apolloError.graphQLErrors?.length > 0) {
    const graphQLError = apolloError.graphQLErrors[0];
    captureMessageInSentry({ error: graphQLError, source: 'sendMessage' });
  }
}

/**
 * Main component for the Coach chat widget that is shown on several of our pages for
 * learners to interact with the Coach AI.
 */
const CoachChatEmbedded = ({
  itemId,
  courseId,
  mode,
  homeView,
  onClose,
  cssOverrides,
  disableSpeechToText = false,
  speechToTextDisabledTooltip = '',
}: Props) => {
  const { expanded: coachChatOpen } = useCourseToolBarPlugins();

  const track = useTracker();

  useEffect(() => {
    return () => {
      // clean up some store state and start fresh for the next mount
      // which may be on the same or a different page
      dropMessages(); // clear messages, these will be refetched on mount anyway
      // unset sessionId in store, will be reset on mount
      // if not cleared we may show data for the wrong page/session
      setChatSessionId(undefined);
    };
  }, []);

  const handleClose = () => {
    onClose?.();

    // used to notify ExternalCoachLauncher when Coach is closed
    document.body.dispatchEvent(new CustomEvent('coachChatClosed'));

    track('interact_coach', {
      coachInteractionType: 'close_coach_tool',
      coach: {
        mode,
        location: 'toolbar',
      },
      ...(courseId && {
        product: {
          type: 'course',
          id: courseId,
        } as Product,
      }),
      ...(itemId && {
        courseItem: {
          id: itemId,
        } as CourseItem,
      }),
    });
  };

  const coachPendoTriggerInstance = getCoachPendoTrigger({ courseId });

  const handlePendoTriggerMessage = () => {
    // for each message sent, check if we need to show the pendo guide
    coachPendoTriggerInstance.handleNewMessage();
  };

  const handlePendoTriggerOpen = useCallback<() => void>(() => {
    // for each time chat is open, check if we need to show the pendo guide
    coachPendoTriggerInstance.handleOpenChat();
  }, [coachPendoTriggerInstance]);

  useEffect(() => {
    if (coachChatOpen) {
      handlePendoTriggerOpen();
    }
  }, [coachChatOpen, handlePendoTriggerOpen]);

  const handleSend = () => {
    handlePendoTriggerMessage();
  };

  const contextConfig = { courseraContext: { courseId, itemId } };

  return (
    <div data-testid="coursera-coach-widget">
      <CoachChat
        enableRichInputField={isCoachAudioEnabled()}
        additionalStyles={cssOverrides}
        showSettings={true}
        trackingData={{
          mode,
          location: 'toolbar',
          courseId,
          itemId,
        }}
        onSend={handleSend}
        emptyState={
          <EmptyState
            sendMessage={(message: string, action: string) => {
              handlePendoTriggerMessage();
              sendMessagePlatform({
                input: {
                  message,
                  config: {
                    coachActionId: action,
                    coachModeId: mode,
                  },
                  ...contextConfig,
                },
              }).catch(handleError);
            }}
            quickMessages={homeView?.quickMessages}
            title={homeView?.title}
            subtitle={homeView?.subtitle}
          />
        }
        mode={mode}
        contextConfig={contextConfig}
        onClose={handleClose}
        disableSpeechToText={disableSpeechToText}
        speechToTextDisabledTooltip={speechToTextDisabledTooltip}
        enableIntentionRouting={isIntentionRoutingEnabled()}
      />
    </div>
  );
};

CoachChatEmbedded.displayName = 'CoachChatEmbedded';

export default CoachChatEmbedded;
